import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import FormField from "../components/forms/FormField";
import LoadingSpinner from "../components/common/LoadingSpinner";
import ErrorMessage from "../components/common/ErrorMessage";
import Logo from "../components/common/Logo";
import { ROUTES, ERROR_MESSAGES } from "../utils/constants";
import firestoreService from "../services/firestoreService";

/**
 * AdminLogin Page Component
 *
 * Provides secure admin login functionality with:
 * - Email/password authentication form
 * - Authentication error handling
 * - Redirect after successful login
 * - Loading states and user feedback
 *
 * Requirements: 3.1, 3.2, 3.3
 */
const AdminLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, loading, error, clearError, isAuthenticated } = useAuth();

  const [formData, setFormData] = useState({
    email: "", // can hold email or username
    password: "",
  });

  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const from = location.state?.from?.pathname || ROUTES.ADMIN_DASHBOARD;
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, location.state]);

  /**
   * Handle form field changes
   * @param {Event} e - Input change event
   */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear field error when user starts typing
    if (formErrors[name]) {
      setFormErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    // Clear auth error when user starts typing
    if (error) {
      clearError();
    }
  };

  /**
   * Validate form fields
   * @returns {boolean} True if form is valid
   */
  const validateForm = () => {
    const errors = {};

    if (!formData.email.trim()) {
      errors.email = ERROR_MESSAGES.REQUIRED_FIELD;
    }

    if (!formData.password.trim()) {
      errors.password = ERROR_MESSAGES.REQUIRED_FIELD;
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /**
   * Handle form submission — supports both email and username login
   * @param {Event} e - Form submit event
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      let loginEmail = formData.email.trim();

      // If the input doesn't contain @, treat it as a username and resolve to email
      if (!loginEmail.includes("@")) {
        const account =
          await firestoreService.getUserAccountByUsername(loginEmail);
        if (!account) {
          setFormErrors({
            email: "Username not found. Try your email address instead.",
          });
          setIsSubmitting(false);
          return;
        }
        loginEmail = account.email;
      }

      await signIn(loginEmail, formData.password);
      // Navigation will be handled by the useEffect hook
    } catch (err) {
      // Error is handled by the AuthContext and displayed via the error state
      console.error("Login error:", err);
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Check if form can be submitted
   * @returns {boolean} True if form can be submitted
   */
  const canSubmit = () => {
    return (
      formData.email.trim() &&
      formData.password.trim() &&
      !isSubmitting &&
      !loading
    );
  };

  return (
    <div className="admin-login-page login-page">
      {/* Page Header */}
      <div className="page-header">
        <div className="container">
          <Logo size="large" className="page-logo" />
          <h1 className="page-title">Mirt Sew</h1>
          <h2 className="page-title-2">Admin Login</h2>
          <p className="page-description">
            Please sign in with your administrator credentials to access the
            dashboard.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="page-content">
        <div className="container">
          <div className="login-form-container">
            <div className="login-card">
              <div className="card-header">
                <h2 className="card-title">Sign In</h2>
              </div>

              <div className="card-body">
                {/* Authentication Error — show mobile app message for regular users */}
                {error && error.includes("REGULAR_USER_WEB_LOGIN") ? (
                  <div
                    style={{
                      background: "rgba(59,130,246,0.08)",
                      border: "1px solid rgba(59,130,246,0.25)",
                      borderRadius: "0.75rem",
                      padding: "1.25rem 1.5rem",
                      textAlign: "center",
                    }}
                  >
                    <div
                      style={{ fontSize: "2.5rem", marginBottom: "0.75rem" }}
                    >
                      📱
                    </div>
                    <p
                      style={{
                        fontWeight: "600",
                        color: "var(--text-primary)",
                        marginBottom: "0.375rem",
                      }}
                    >
                      Please use the MirtSew mobile app
                    </p>
                    <p
                      style={{
                        fontSize: "0.875rem",
                        color: "var(--text-secondary)",
                      }}
                    >
                      Your account is registered as a regular member. Log in
                      using the MirtSew mobile application to access your
                      account.
                    </p>
                  </div>
                ) : error ? (
                  <div className="form-message error-section">
                    <ErrorMessage message={error} className="auth-error" />
                  </div>
                ) : null}

                {/* Login Form */}
                <form onSubmit={handleSubmit} className="login-form" noValidate>
                  {/* Email Field */}
                  <FormField
                    label="Email or Username"
                    name="email"
                    type="text"
                    value={formData.email}
                    onChange={handleInputChange}
                    error={formErrors.email}
                    required
                    autoComplete="username"
                    placeholder="Email address or username"
                    disabled={isSubmitting || loading}
                  />

                  {/* Password Field */}
                  <FormField
                    label="Password"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    error={formErrors.password}
                    required
                    autoComplete="current-password"
                    placeholder="Enter your password"
                    disabled={isSubmitting || loading}
                  />

                  {/* Submit Button */}
                  <div className="form-actions">
                    <button
                      type="submit"
                      className={`submit-button ${
                        canSubmit() ? "enabled" : "disabled"
                      }`}
                      disabled={!canSubmit()}
                    >
                      {isSubmitting || loading ? (
                        <>
                          <LoadingSpinner size="small" />
                          <span>Signing In...</span>
                        </>
                      ) : (
                        "Sign In"
                      )}
                    </button>
                  </div>
                  <div style={{ textAlign: "center", marginTop: "0.75rem" }}>
                    <button
                      type="button"
                      onClick={() => navigate(ROUTES.FORGOT_PASSWORD)}
                      style={{
                        background: "none",
                        border: "none",
                        color: "var(--color-primary)",
                        cursor: "pointer",
                        textDecoration: "underline",
                        fontSize: "0.875rem",
                      }}
                    >
                      Forgot password?
                    </button>
                  </div>
                </form>
              </div>
            </div>

            {/* Additional Information */}
            <div className="login-info">
              <div className="info-card">
                <h3 className="info-title">Administrator Access</h3>
                <p className="info-text">
                  This login is restricted to authorized administrators only. If
                  you are a registered user, please use the main application
                  login.
                </p>
              </div>

              <div className="info-card">
                <h3 className="info-title">Security Notice</h3>
                <ul className="info-list">
                  <li>Your session will expire after a period of inactivity</li>
                  <li>
                    Always log out when finished using the admin dashboard
                  </li>
                  <li>
                    Report any suspicious activity to the system administrator
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
